import { CODIGOS_ERRO, PREFIXO_API, ROTAS } from '@remoto/shared';
import { usarSessao, type UsuarioLogado } from './estado/sessao';

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly detalhes?: unknown,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }
}

export interface SessaoRecebida {
  acesso: string;
  expiraEm: string;
  usuario: UsuarioLogado;
  codigosRecuperacao?: string[];
}

interface Opcoes extends Omit<RequestInit, 'body'> {
  corpo?: unknown;
  /** Rotas de entrada não devem tentar renovar a sessão: ainda não há sessão. */
  semRenovar?: boolean;
}

async function requisitar(caminho: string, opcoes: Opcoes): Promise<Response> {
  const { corpo, headers, semRenovar: _semRenovar, ...resto } = opcoes;
  const acesso = usarSessao.getState().acesso;
  return fetch(PREFIXO_API + caminho, {
    ...resto,
    credentials: 'include',
    headers: {
      ...(corpo !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(acesso ? { authorization: 'Bearer ' + acesso } : {}),
      ...headers,
    },
    ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {}),
  });
}

/**
 * Cliente HTTP do painel. Erros viram `ErroApi` com o código do servidor, para
 * a tela escolher a mensagem. Um 401 dispara uma tentativa de renovação pelo
 * cookie e repete a chamada uma única vez.
 */
export async function chamar<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  let resposta = await requisitar(caminho, opcoes);

  if (resposta.status === 401 && !opcoes.semRenovar && usarSessao.getState().acesso) {
    const renovou = await renovarSessao();
    if (renovou) resposta = await requisitar(caminho, opcoes);
  }

  if (resposta.status === 204) return undefined as T;

  const dados: unknown = await resposta.json().catch(() => ({}));
  if (!resposta.ok) {
    const d = dados as { codigo?: string; mensagem?: string; detalhes?: unknown };
    throw new ErroApi(
      resposta.status,
      d.codigo ?? 'erro',
      d.mensagem ?? 'nao foi possivel completar a operacao',
      d.detalhes,
    );
  }
  return dados as T;
}

/**
 * A renovação em curso, se houver uma.
 *
 * Módulo, e não componente: o que precisa ser único é a **requisição**, e ela
 * não pertence a nenhuma tela. Sem isto, cada 401 disparava o seu próprio
 * `POST /auth/refresh`, e o painel produz 401 em rajada, não um de cada vez —
 * `refetchOnWindowFocus`, a invalidação geral quando o socket volta, a
 * autenticação do socket e quatro `beforeLoad` de rota disparam juntos assim
 * que o aparelho acorda. Com rotação no servidor, o segundo pedido apresenta
 * um cookie já gasto: o servidor concluía roubo e derrubava a sessão.
 */
let renovacaoEmVoo: Promise<boolean> | null = null;

/** Espera curta, para o cookie do vencedor chegar ao pote do navegador. */
function esperar(ms: number): Promise<void> {
  return new Promise((pronto) => setTimeout(pronto, ms));
}

async function girarSessao(segundaTentativa: boolean): Promise<boolean> {
  try {
    const resposta = await fetch(PREFIXO_API + ROTAS.auth.refresh, {
      method: 'POST',
      credentials: 'include',
    });

    if (resposta.ok) {
      const sessao = (await resposta.json()) as SessaoRecebida;
      usarSessao.getState().entrar(sessao.acesso, sessao.expiraEm, sessao.usuario);
      return true;
    }

    /*
      Outra aba girou o cookie no mesmo instante.

      Não é sessão perdida: o vencedor já gravou o cookie novo, que é
      compartilhado por todas as abas. Uma segunda tentativa curta pega o
      cookie novo e renova. Deslogar aqui seria desfazer, na aba de trás, o
      login que a da frente acabou de renovar.
    */
    const corpo: unknown = await resposta.json().catch(() => ({}));
    const codigo = (corpo as { codigo?: string }).codigo;
    if (codigo === CODIGOS_ERRO.renovacaoConcorrente && !segundaTentativa) {
      await esperar(250);
      return girarSessao(true);
    }

    // Cobre o aparelho que estava FECHADO quando o socket caiu: sem conexão
    // não houve `conta.derrubada`, e é o refresh que descobre a substituição.
    usarSessao.getState().sair(codigo === CODIGOS_ERRO.sessaoSubstituida ? 'substituida' : undefined);
    return false;
  } catch {
    // Rede fora do ar não é credencial inválida: manter a sessão em memória
    // deixa a próxima tentativa funcionar sem passar pela tela de login.
    return false;
  }
}

/**
 * Renova a sessão pelo cookie — **uma vez só**, por mais gente que peça.
 *
 * Quem chegar enquanto uma renovação está no ar recebe a mesma promessa. É o
 * conserto do lado do cliente para a rajada de 401; o do lado do servidor, que
 * cobre abas e aparelhos diferentes (que nenhum cliente consegue coordenar),
 * é a janela de tolerância do `girarRefresh`.
 */
export function renovarSessao(): Promise<boolean> {
  renovacaoEmVoo ??= girarSessao(false).finally(() => {
    renovacaoEmVoo = null;
  });
  return renovacaoEmVoo;
}

/** Garante uma sessão utilizável antes de carregar uma rota protegida. */
export async function garantirSessao(): Promise<boolean> {
  const estado = usarSessao.getState();
  if (estado.acesso && !estado.precisaRenovar()) return true;
  return renovarSessao();
}
